import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { CheckCircle, XCircle } from "lucide-react";
import { FC } from "react";
import { CellProps, Column } from "react-table";

import { getOrganisations } from "api/organisations";
import { organisationsKeys } from "api/organisations/queries";
import { OrganisationsType } from "api/organisations/types";
import { QueryParamsType } from "api/utils";
import { hasPermissions } from "components/stores/UserStore";
import Table from "components/Table";
import useRemoteTableLogic from "components/Table/useRemoteTableLogic";
import { useModal } from "utils/hooks/useModal";

// import DeleteUserModal from "./components/DeleteUserModal";
// import EditUserModal from "./components/EditUserModal";
// import NewUserModal from "./components/NewUserModal";
import EditOrganisationModal from "./components/EditOrganisationModal";
import OrganisationsActionsFormatter from "./formatters/OrganisationsActionsFormatter";
import { useOrganisationsFilter } from "./useOrganisationsFilter";

interface OrganisationsTableProps {
  filters?: QueryParamsType<OrganisationsType>;
}

const OrganisationsTable: FC<OrganisationsTableProps> = ({
  filters: filtersFromProps,
}) => {
  const filterHook = useOrganisationsFilter();
  const filters = filtersFromProps ?? filterHook.filters;

  const {
    // isOpen: isOpenDelete,
    // handleClose: handleCloseDelete,
    handleOpen: handleOpenDelete,
    // context: deleteContext
  } = useModal<OrganisationsType>();

  const {
    isOpen: isEditOpen,
    handleClose: handleCloseEdit,
    handleOpen: handleOpenEdit,
    context: editContext,
  } = useModal<OrganisationsType>();

  const { data, status, sortOptions, paginationOptions } = useRemoteTableLogic(
    organisationsKeys.list(),
    getOrganisations,
    filters,
  );

  const columns: Column<OrganisationsType>[] = [
    {
      accessor: "name",
      Header: "Organisation Name",
    },
    {
      accessor: "type",
      Header: "Type",
      Cell: ({ value }: CellProps<OrganisationsType>) => (
        <Box textTransform="capitalize">{value}</Box>
      ),
    },
    {
      accessor: "email",
      Header: "Organisation Email",
    },
    {
      accessor: "active",
      Header: "Active",
      Cell: ({ value }: CellProps<OrganisationsType>) => {
        const isActive = Boolean(value);
        return (
          <Tooltip
            title={
              isActive ? "Organisation is active" : "Organisation is inactive"
            }
            arrow
            placement="top"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                color: (t) =>
                  isActive
                    ? ((t.palette as any).brandSuccess?.primary ?? "#2CB666")
                    : ((t.palette as any).brandText?.secondary ?? "#A2A8B6"),
              }}
            >
              {isActive ? <CheckCircle size={18} /> : <XCircle size={18} />}
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "inherit" }}
              >
                {isActive ? "Active" : "Inactive"}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      accessor: "leadSource",
      Header: "Lead Source",
    },
    {
      accessor: "brand",
      Header: "Brand",
    },
    {
      id: "partnerPayout",
      Header: "Partner Payout",
      Cell: ({ row }: CellProps<OrganisationsType>) => {
        if (row.original.partnerPayout.type === "they-invoice") {
          return <Box>They Invoice</Box>;
        } else if (row.original.partnerPayout.type === "percentage") {
          return <Box>{`${row.original.partnerPayout.value}%`}</Box>;
        }

        return <Box></Box>;
      },
    },

    // {
    //   accessor: "createdAt",
    //   Header: "Date"
    // },
    {
      id: "actions",
      Header: "Actions",
      Cell: ({ cell }: CellProps<OrganisationsType>) => (
        <OrganisationsActionsFormatter
          {...cell}
          handleOpenEdit={handleOpenEdit}
          handleOpenDelete={handleOpenDelete}
        />
      ),
      align: "right",
    },
  ];

  return (
    <Stack spacing={2} flex={1} height="100%" overflow="hidden">
      <Table
        data={data}
        columns={columns}
        sort={sortOptions}
        pagination={paginationOptions}
        status={status}
      />
      {hasPermissions("update:organisations") && (
        <>
          <EditOrganisationModal
            isOpen={isEditOpen}
            handleClose={handleCloseEdit}
            organisation={editContext}
          />
        </>
      )}
      {hasPermissions("delete:organisations") && (
        <>
          {/* <DeleteUserModal
            isOpen={isOpenDelete}
            handleClose={handleCloseDelete}
            user={deleteContext}
          /> */}
        </>
      )}
    </Stack>
  );
};

export default OrganisationsTable;
